import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import { Button } from '../components/ui/Button';
import { LanguageSwitcher } from '../components/layout/LanguageSwitcher';
import { IconArrowRight, IconUpload, IconWave, IconStore } from '../components/ui/icons';
import type { ComponentType } from 'react';

function Step({ n, Icon, title, body }: { n: string; Icon: ComponentType<{ size?: number }>; title: string; body: string }) {
  return (
    <div className="rounded border border-border-strong bg-surface-2 p-6">
      <div className="mb-4 flex items-center justify-between">
        <span className="type-data text-muted">{n}</span>
        <span className="text-secondary">
          <Icon size={22} />
        </span>
      </div>
      <h3 className="type-heading-2 text-primary">{title}</h3>
      <p className="type-body text-secondary mt-2">{body}</p>
    </div>
  );
}

export function Landing() {
  const { t } = useI18n();
  return (
    <div className="flex min-h-dvh flex-col bg-bg">
      <header className="mx-auto flex w-full max-w-content items-center justify-between px-4 py-4 sm:px-6">
        <div className="flex items-baseline gap-2">
          <span className="type-heading-2 text-primary">{t.common.brand}</span>
          <span className="type-caption text-muted hidden sm:inline">{t.common.tagline}</span>
        </div>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <Link to="/auth">
            <Button variant="ghost" size="md">
              {t.landing.ctaSecondary}
            </Button>
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-content flex-1 px-4 sm:px-6">
        {/* Hero */}
        <section className="grid items-center gap-8 py-12 lg:grid-cols-2 lg:py-20">
          <div>
            <p className="type-label text-accent mb-4">{t.landing.heroKicker}</p>
            <h1 className="type-display-xl text-primary whitespace-pre-line">{t.landing.heroTitle}</h1>
            <p className="type-body text-secondary mt-5 max-w-md">{t.landing.heroBody}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/auth?mode=signup">
                <Button size="lg" iconRight={<IconArrowRight size={18} />}>
                  {t.landing.ctaPrimary}
                </Button>
              </Link>
              <Link to="/market">
                <Button variant="secondary" size="lg">
                  {t.nav.market}
                </Button>
              </Link>
            </div>
          </div>

          {/* Decorative waveform panel — data-as-decoration is the one allowed motif */}
          <div className="rounded border border-border-strong bg-surface-1 p-6">
            <div className="mb-4 flex items-center justify-between">
              <span className="type-label text-secondary">WAVE · 24-BIT · 140 BPM</span>
              <span className="type-caption text-muted tnum">2:34</span>
            </div>
            <div className="flex h-40 items-center gap-[3px]" aria-hidden>
              {Array.from({ length: 56 }, (_, i) => {
                const h = 20 + Math.round(60 * Math.abs(Math.sin(i * 0.7)) + (i % 5) * 4);
                const hot = i < 20;
                return (
                  <span
                    key={i}
                    className={hot ? 'w-full bg-wave-hot' : 'w-full bg-wave-rest'}
                    style={{ height: `${Math.min(100, h)}%` }}
                  />
                );
              })}
            </div>
            <p className="type-caption text-muted mt-4">{t.landing.machineShowsWork}</p>
          </div>
        </section>

        {/* Three steps */}
        <section className="grid gap-4 pb-16 md:grid-cols-3">
          <Step n="01" Icon={IconUpload} title={t.landing.step1Title} body={t.landing.step1Body} />
          <Step n="02" Icon={IconWave} title={t.landing.step2Title} body={t.landing.step2Body} />
          <Step n="03" Icon={IconStore} title={t.landing.step3Title} body={t.landing.step3Body} />
        </section>
      </main>

      <footer className="border-t border-border-strong">
        <div className="mx-auto flex w-full max-w-content flex-wrap items-center justify-between gap-2 px-4 py-6 sm:px-6">
          <span className="type-caption text-muted">
            {t.common.brand} · {t.landing.footerRights}
          </span>
          <LanguageSwitcher />
        </div>
      </footer>
    </div>
  );
}
